import React, { useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import moment from 'moment';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetResearchParticipantsQuery } from '../../api/researchApi.js';
import { setSelectedUserId } from '../../actions/userActions.js';
import { isAdminSelector } from '../../selectors/authSelectors.js';

const PAGE_SIZE = 50;

const STATUS_PRESENTATION = {
  NEW: { label: 'Applied', color: 'info' },
  INVITED: { label: 'Project in flight', color: 'secondary' },
  WORK_SUBMITTED: { label: 'Under review', color: 'warning' },
  REJECTED: { label: 'Not selected', color: 'default' },
  WORK_ACCEPTED: { label: 'Accepted', color: 'success' },
  PAID: { label: 'Paid', color: 'success' }
};

const getErrorMessage = (error) => error?.data?.msg || error?.error || 'Could not load research applicants.';

const ResearchApplicantsPage = () => {
  const { studySlug = '' } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAdmin = useSelector(isAdminSelector);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { data, error, isFetching, isLoading } = useGetResearchParticipantsQuery(
    {
      studySlug,
      search,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE
    },
    { skip: !studySlug || !isAdmin }
  );

  if (!isAdmin) {
    return <Navigate to='/dashboard' replace />;
  }

  const participants = data?.participants ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openParticipant = (participant) => {
    dispatch(setSelectedUserId(participant.user.id));
    navigate(`/dashboard?userId=${participant.user.id}&section=Research`);
  };

  return (
    <Box data-testid='research-applicants-page' sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar position='static' color='inherit' elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ gap: 1.5 }}>
          <Typography sx={{ color: 'navy.main', fontWeight: 700 }}>keeper</Typography>
          <Typography variant='body2'>research</Typography>
          <Chip label='ADMIN' size='small' variant='outlined' />
        </Toolbar>
      </AppBar>

      <Container maxWidth='lg' sx={{ py: { xs: 3, md: 5 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
          Back to dashboard
        </Button>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ alignItems: { md: 'flex-end' }, justifyContent: 'space-between', gap: 2, mb: 2.5 }}
        >
          <Box>
            <Typography component='h1' variant='h4' sx={{ fontWeight: 600 }}>
              Applicants
            </Typography>
            <Typography color='text.secondary'>
              {total} {total === 1 ? 'applicant' : 'applicants'} for {studySlug}
            </Typography>
          </Box>
          <Box component='form' role='search' onSubmit={submitSearch} sx={{ width: { xs: '100%', md: 360 } }}>
            <TextField
              type='search'
              fullWidth
              size='small'
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder='Search applicants'
              slotProps={{
                htmlInput: { 'aria-label': 'Search applicants' },
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
        </Stack>

        {error && <Alert severity='error'>{getErrorMessage(error)}</Alert>}
        {!error && (
          <TableContainer component={Paper} variant='outlined'>
            <Table aria-label={`${studySlug} research applicants`}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'aliceBlue.main' }}>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell padding='checkbox' />
                </TableRow>
              </TableHead>
              <TableBody>
                {(isLoading || isFetching) && participants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align='center' sx={{ py: 6 }}>
                      <CircularProgress size={28} aria-label='Loading applicants' />
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && participants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align='center' sx={{ py: 6, color: 'text.secondary' }}>
                      {search ? 'No applicants match this search.' : 'No applicants yet.'}
                    </TableCell>
                  </TableRow>
                )}
                {participants.map((participant) => {
                  const status =
                    participant.status === 'INVITED' && (participant.revisionNotes || participant.revisionRequestedAt)
                      ? { label: 'Edits requested', color: 'warning' }
                      : (STATUS_PRESENTATION[participant.status] ?? {
                          label: participant.status,
                          color: 'default'
                        });
                  const fullName = [participant.user.firstName, participant.user.lastName].filter(Boolean).join(' ');
                  const secondary = participant.user.location || participant.user.email || participant.user.phone;

                  return (
                    <TableRow
                      hover
                      key={participant.id}
                      tabIndex={0}
                      aria-label={`Open ${fullName}`}
                      onClick={() => openParticipant(participant)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openParticipant(participant);
                        }
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          {fullName || `User ${participant.user.id}`}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {secondary || `User ${participant.user.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={status.label} color={status.color} size='small' variant='outlined' />
                      </TableCell>
                      <TableCell>{moment(participant.createdAt).format('MMM D, YYYY')}</TableCell>
                      <TableCell padding='checkbox'>
                        <IconButton aria-label={`Open ${fullName}`} size='small' tabIndex={-1}>
                          <ChevronRightIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {pageCount > 1 && (
          <Pagination
            page={page}
            count={pageCount}
            onChange={(_event, nextPage) => setPage(nextPage)}
            sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}
          />
        )}
      </Container>
    </Box>
  );
};

export default ResearchApplicantsPage;
